import {
  CURVE_SEGMENTS,
  DIAGRAM_ZONE_COLOR_LABELS,
  curvePoint,
  flattenPolyline,
  flattenSmooth,
  type ArrowGeometry,
  type DiagramElement,
  type DiagramPitch,
  type DiagramStep,
} from '@ukinfo/shared';
import { HIT_STROKE, HIT_ZONE_STROKE, hitRadius, hitSize, type FieldPoint } from './diagramEdit';
import { ELEMENT_NAMES } from './DiagramSelectionBar';
import { ARROW_LABELS, LAYER, PITCH_HEIGHT, TEAM_STYLE, drawingGeometry, textLayout, zoneTrianglePoints } from './PitchDiagram';

/**
 * Der Stapel unter einem Tipp: alle Elemente, deren Trefferflaeche den Punkt enthaelt - ohne React, damit sich die Rechnung einzeln
 * pruefen laesst. Sie bildet die unsichtbaren Trefferflaechen des Editors (`HitShape`) nach und braucht deshalb kein DOM.
 *
 * Reihenfolge: kleine Punkt-Elemente (Spieler, Huetchen, Ball, Stange, Ring, Huerde, Tor, Text) vor Pfeilen und Masslinien, diese vor
 * Zonen; innerhalb gleicher Art das zuoberst gezeichnete zuerst (Zeichenebene, dann spaeter in der Liste = weiter oben).
 */

export interface StackContext {
  pitch: DiagramPitch;
  /** Zoomstufe der Ansicht: Trefferflaechen bleiben auf dem Bildschirm gleich gross. */
  zoom: number;
  /** Symbolgroesse der Skizze (fuer die Groesse eines Texts). */
  scale: number;
}

type Pt = { x: number; y: number };

/** Gruppe in der Stapelreihenfolge: 0 Punkt-Elemente, 1 Pfeile und Masslinien, 2 Zonen. */
function kindRank(element: DiagramElement): number {
  if (element.type === 'zone') return 2;
  return element.type === 'arrow' || element.type === 'measure' ? 1 : 0;
}

/** Abstand eines Punkts zur Strecke ab. */
function segmentDistance(p: Pt, a: Pt, b: Pt): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const span = dx * dx + dy * dy;
  const t = span === 0 ? 0 : Math.min(1, Math.max(0, ((p.x - a.x) * dx + (p.y - a.y) * dy) / span));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Der Weg eines Pfeils als abgetasteter Linienzug in Zeichnungseinheiten (wie er gezeichnet wird: Kurve, Knicke, weich). */
function pathPoints(geometry: ArrowGeometry): Pt[] {
  if (geometry.points && geometry.points.length > 0) {
    const vertices = [geometry.from, ...geometry.points, geometry.to];
    return geometry.smooth ? flattenSmooth(vertices) : flattenPolyline(vertices);
  }
  if (geometry.via) return Array.from({ length: CURVE_SEGMENTS + 1 }, (_, index) => curvePoint(geometry, index / CURVE_SEGMENTS));
  return [geometry.from, geometry.to];
}

function insidePolygon(p: Pt, corners: readonly Pt[]): boolean {
  let inside = false;
  for (let i = 0, j = corners.length - 1; i < corners.length; j = i, i += 1) {
    const a = corners[i]!;
    const b = corners[j]!;
    if (a.y > p.y !== b.y > p.y && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Trifft der Punkt `p` (Zeichnungseinheiten) die Trefferflaeche des Elements? */
function hits(element: DiagramElement, p: Pt, height: number, context: StackContext): boolean {
  switch (element.type) {
    case 'zone': {
      // Die Form selbst (Kreis, Dreieck): was ausserhalb im Rechteck liegt, trifft das Element darunter. Dazu der Rand mit seiner Trefferbreite.
      const margin = hitSize(HIT_ZONE_STROKE, context.zoom) / 2;
      const x = element.x;
      const y = (element.y / 100) * height;
      const w = element.w;
      const h = (element.h / 100) * height;
      const shape = element.shape ?? 'rect';
      if (shape === 'circle') {
        const rx = w / 2 + margin;
        const ry = h / 2 + margin;
        return ((p.x - (x + w / 2)) / rx) ** 2 + ((p.y - (y + h / 2)) / ry) ** 2 <= 1;
      }
      if (shape === 'triangle') {
        const corners = zoneTrianglePoints(x, y, w, h, element.rotation ?? 0)
          .split(' ')
          .map((pair) => {
            const [cx, cy] = pair.split(',').map(Number);
            return { x: cx!, y: cy! };
          });
        return insidePolygon(p, corners) || corners.some((corner, index) => segmentDistance(p, corner, corners[(index + 1) % corners.length]!) <= margin);
      }
      return p.x >= x - margin && p.x <= x + w + margin && p.y >= y - margin && p.y <= y + h + margin;
    }
    case 'arrow':
    case 'measure': {
      const reach = hitSize(HIT_STROKE, context.zoom) / 2;
      const points = pathPoints(drawingGeometry(element, height));
      if (points.length === 1) return Math.hypot(p.x - points[0]!.x, p.y - points[0]!.y) <= reach;
      return points.some((point, index) => index > 0 && segmentDistance(p, points[index - 1]!, point) <= reach);
    }
    case 'text': {
      const box = textLayout(element, height, context.scale);
      const pad = hitSize(1.6, context.zoom);
      return Math.abs(p.x - box.cx) <= box.w / 2 + pad && Math.abs(p.y - box.cy) <= box.h / 2 + pad;
    }
    default:
      return Math.hypot(p.x - element.x, p.y - (element.y / 100) * height) <= hitRadius(element, context.zoom);
  }
}

/**
 * Indizes aller Elemente unter dem Feldpunkt, das zuerst zu waehlende vorn. `forced` ist ein Element, das nachweislich getroffen wurde
 * (die Trefferflaeche im Bild), auch wenn die Rechnung es knapp verfehlt: es steht dann in seiner Gruppe mit im Stapel.
 */
export function stackAt(elements: readonly DiagramElement[], point: FieldPoint, context: StackContext, forced: number | null = null): number[] {
  const height = PITCH_HEIGHT[context.pitch];
  const p = { x: point.x, y: (point.y / 100) * height };
  const found: number[] = [];
  elements.forEach((element, index) => {
    if (index === forced || hits(element, p, height, context)) found.push(index);
  });
  return found.sort((a, b) => {
    const left = elements[a]!;
    const right = elements[b]!;
    // Das im Bild getroffene Element steht in seiner Gruppe vorn (im Bild liegt es dort ohnehin zuoberst).
    return kindRank(left) - kindRank(right) || Number(b === forced) - Number(a === forced) || LAYER[right.type] - LAYER[left.type] || b - a;
  });
}

/** Der Name, den die Auswahlleiste fuer die Art eines Elements nutzt, ergaenzt um das, was es von seinesgleichen unterscheidet. */
function nameOf(element: DiagramElement, stepNumber: number | null): string {
  switch (element.type) {
    case 'player':
      return element.label ? `${ELEMENT_NAMES.player} ${element.label}` : `${ELEMENT_NAMES.player} (${TEAM_STYLE[element.team].name})`;
    case 'arrow':
      return stepNumber === null ? ARROW_LABELS[element.kind] : `${ARROW_LABELS[element.kind]} ${stepNumber}`;
    case 'measure':
      return element.label ? `${ELEMENT_NAMES.measure} ${element.label}` : ELEMENT_NAMES.measure;
    case 'zone':
      return element.color ? `${ELEMENT_NAMES.zone} (${DIAGRAM_ZONE_COLOR_LABELS[element.color]})` : ELEMENT_NAMES.zone;
    case 'text': {
      const text = element.text.length > 16 ? `${element.text.slice(0, 15)}…` : element.text;
      return `${ELEMENT_NAMES.text} „${text}“`;
    }
    default:
      return ELEMENT_NAMES[element.type];
  }
}

/**
 * Namen der Elemente `indices` fuer die Liste "Hier liegen": die Bezeichnungen der Auswahlleiste ("Spieler 7", "Hütchen"), bei Pfeilen
 * mit der Schrittnummer, die im Feld daran steht ("Dribbling 1"). Gleiche Namen bekommen eine Nummer ("Hütchen (1)", "Hütchen (2)"),
 * damit jede Zeile eindeutig bleibt.
 */
export function stackNames(elements: readonly DiagramElement[], indices: readonly number[], steps: readonly DiagramStep[]): string[] {
  // Schrittnummern zeigt das Feld erst ab zwei Schritten.
  const numbered = steps.length >= 2;
  const stepOf = (index: number): number | null => (numbered ? (steps.find((step) => step.arrows.includes(index))?.number ?? null) : null);
  const names = indices.map((index) => {
    const element = elements[index];
    return element ? nameOf(element, stepOf(index)) : '';
  });
  const total = new Map<string, number>();
  for (const name of names) total.set(name, (total.get(name) ?? 0) + 1);
  const seen = new Map<string, number>();
  return names.map((name) => {
    if ((total.get(name) ?? 0) < 2) return name;
    const count = (seen.get(name) ?? 0) + 1;
    seen.set(name, count);
    return `${name} (${count})`;
  });
}
